import React, { useEffect, useRef, useState } from 'react';
import { UtensilsCrossed, ChefHat, Cake, Wine, Coffee, Fish, Leaf, Clock } from 'lucide-react';
import { restaurantDetails, openingHours, siteImages } from '../data/mock';

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.15, ...options });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, isInView];
};

const Restaurant = () => {
  const [heroRef, heroInView] = useInView();
  const [aboutRef, aboutInView] = useInView();
  const [highlightRef, highlightInView] = useInView();
  const [hoursRef, hoursInView] = useInView();

  const highlights = [
    { icon: Fish, title: "Frischer Fisch", text: "Täglich frischer Fisch und Meeresfrüchte" },
    { icon: Leaf, title: "Frische Kräuter", text: "Gemüse, Obst, Knoblauch und mediterrane Kräuter" },
    { icon: Coffee, title: "Kaffee & Kuchen", text: "Hausgemachte Waffeln, Torten und Kuchen" },
    { icon: Wine, title: "Weine & Cocktails", text: "Große Wein- und Getränke-Auswahl" },
  ];

  return (
    <div className="bg-[#0f0704]">
      {/* Hero Section */}
      <section ref={heroRef} className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 hero-parallax"
          style={{
            backgroundImage: `url('${siteImages.restaurantHero}')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#0f0704]" />
        <div className={`relative z-10 text-center px-6 ${heroInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <span className="text-amber-400 text-sm uppercase tracking-[0.35em] font-medium">
            Unser Lokal
          </span>
          <h1 className="font-brand text-4xl md:text-6xl lg:text-7xl text-white mt-4">
            Das Restaurant
          </h1>
        </div>
      </section>

      {/* About Section */}
      <section ref={aboutRef} className="py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6">
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${aboutInView ? 'opacity-100' : 'opacity-0'}`}>
            <div className={aboutInView ? 'animate-slide-left' : ''}>
              <span className="text-amber-400 text-sm uppercase tracking-[0.3em] font-medium">
                Über uns
              </span>
              <h2 className="font-brand text-3xl md:text-4xl text-white mt-4 mb-8 leading-tight">
                {restaurantDetails.intro}
              </h2>
              <div className="space-y-5 text-white/60 leading-relaxed">
                <p>{restaurantDetails.description}</p>
                <p>{restaurantDetails.coffeeText}</p>
                <p>{restaurantDetails.welcomeText}</p>
                <p className="font-playfair italic text-amber-400/80 text-lg">
                  {restaurantDetails.teamText}
                </p>
              </div>
            </div>

            <div className={aboutInView ? 'animate-slide-right' : ''}>
              <div className="relative">
                <div className="img-hover-zoom rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={siteImages.restaurantAbout}
                    alt="Cappuccino"
                    className="w-full h-[400px] lg:h-[500px] object-cover"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-amber-400/30 rounded-2xl -z-0" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights Section */}
      <section ref={highlightRef} className="py-24 bg-[#130905]">
        <div className="max-w-6xl mx-auto px-6">
          <div className={`text-center mb-16 ${highlightInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <span className="text-amber-400 text-sm uppercase tracking-[0.3em] font-medium">
              Qualität & Frische
            </span>
            <h2 className="font-brand text-3xl md:text-4xl text-white mt-4">
              Unser Programm
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className={`group relative bg-white/[0.03] border border-white/[0.06] rounded-2xl p-8 hover:bg-white/[0.06] hover:border-amber-400/20 transition-all duration-500 ${highlightInView ? 'animate-fade-in-up' : 'opacity-0'}`}
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className="w-12 h-12 mb-5 rounded-xl bg-amber-400/10 flex items-center justify-center group-hover:bg-amber-400/20 transition-colors duration-300">
                    <Icon size={24} className="text-amber-400" />
                  </div>
                  <h3 className="font-playfair text-lg font-semibold text-white mb-2">
                    {item.title}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Opening Hours Section */}
      <section ref={hoursRef} className="py-24 bg-[#130905]">
        <div className="max-w-2xl mx-auto px-6">
          <div className={`text-center ${hoursInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="w-14 h-14 mx-auto mb-6 rounded-xl bg-amber-400/10 flex items-center justify-center">
              <Clock size={26} className="text-amber-400" />
            </div>
            <h2 className="font-brand text-3xl md:text-4xl text-white mb-10">
              Öffnungszeiten
            </h2>
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-8">
              {openingHours.map((item) => (
                <div
                  key={item.day}
                  className="flex justify-between items-center py-3 border-b border-white/[0.06] last:border-b-0"
                >
                  <span className="text-white/60 font-medium">{item.day}</span>
                  <span className={`font-medium ${
                    item.hours === 'Ruhetag'
                      ? 'text-red-400/80'
                      : 'text-amber-400'
                  }`}>
                    {item.hours}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Restaurant;
